'use strict';

import { isCreativeMode } from '@core/utils/utils';
import {
    SEARCH_IMGS_ORDER_BY_OPTIONS,
    DEFAULT_ORDER_BY_OPTION,
    SEARCHER_TYPES,
} from '@core/constants/SEARCHER';
import { SEARCH_IMG_QUERY_PARAMS } from '@core/constants/API';
import {
    updateFiltersStateHistoryPage,
    updateFiltersStateHistoryPageReport,
} from '@core/utils/history-state';
import { getCurrentSearcherObj, dispatchDetailIbj } from '@core/utils/searcher';
import { updateStoreFiltersState } from '@core/utils/store';
import { STORE_SEARCHER_FILTERS } from '@core/constants/STORE';
import { UPDATE_AI_SEARCHER_EVENT } from '@core/constants/EVENTS';
import { isDetailLightboxView, isReportDetailsView } from '@core/utils/report';
import { SUB_STRING_CODE } from '@core/constants/CONSTANTS';

import { ORDER_BY_OPTIONS, UPDATE_VALUE_ORDER_BY_EVENT } from './CONSTANTS';

/**
 * Update the order by button label and selected state.
 * @param {HTMLElement} currentOption - The current option element.
 * @param {string} optionValue - The option value.
 * @param {NodeListOf<HTMLElement>} allOptions - All order by options.
 * @param {HTMLElement} orderByButton - The order by button element.
 * @returns {void}
 */
export const updateOrderButtonLabel = (
    currentOption,
    optionValue,
    allOptions,
    orderByButton,
) => {
    allOptions.forEach((option) => {
        option.classList.remove('selected');
    });

    currentOption.classList.add('selected');
    orderByButton.innerHTML = currentOption.innerHTML;
    orderByButton.setAttribute('value', optionValue);
};

/**
 * Initialize order by options in the DOM.
 * @param {HTMLElement} orderByOptionsContainer - The container element.
 * @param {Array<string>} orderOptions - The order options to display.
 * @returns {void}
 */
export const initOrderByOptions = (orderByOptionsContainer, orderOptions) => {
    orderByOptionsContainer.innerHTML = ORDER_BY_OPTIONS.filter((option) =>
        orderOptions.includes(option.id),
    )
        .map(
            (option) =>
                `<li value="${option.value}"><span class="label-medium" data-translate="results.orderBy.${option.id}">${option.label}</span></li>`,
        )
        .join('');
};

/**
 * Show order by options with proper visibility and selection.
 * @param {NodeListOf<HTMLLIElement>} orderByOptions - The order by options elements.
 * @param {string} currentOrderByOption - The current selected option.
 * @returns {void}
 */
export const showOrderByOptions = (orderByOptions, currentOrderByOption) => {
    orderByOptions.forEach((option) => {
        option.classList.remove('selected');
        if (option.getAttribute('value') === currentOrderByOption) {
            option.classList.add('selected');
        }

        if (
            (option.getAttribute('value') === SEARCH_IMGS_ORDER_BY_OPTIONS.CAP &&
                !isReportDetailsView()) ||
            (option.getAttribute('value') === SEARCH_IMGS_ORDER_BY_OPTIONS.SIM &&
                isDetailLightboxView())
        ) {
            option.style.display = 'none';
        } else {
            option.style.display = 'list-item';
        }
    });
};

/**
 * Update the current searcher object with the new order by value.
 * @param {string} currentOrderByOption - The current order by option.
 * @returns {void}
 */
export const updateCurrentSearcherObj = (currentOrderByOption) => {
    const currentSearcherObj = getCurrentSearcherObj();

    if (!currentSearcherObj) return;

    currentSearcherObj[STORE_SEARCHER_FILTERS.ORDER_BY_VALUE] = currentOrderByOption;

    const urlParams = new URLSearchParams(window.location.search);
    updateStoreFiltersState(
        urlParams.get(SEARCHER_TYPES.SEARCHER_ID),
        currentSearcherObj,
    );
};

/**
 * Check and update the current searcher object based on URL parameters.
 * @param {boolean} isReportDetailsView - Whether this is a report details view.
 * @param {boolean} isReportsView - Whether this is a reports view.
 * @returns {string} The current order by option.
 */
export const checkCurrentSearcherObj = (isReportDetailsView, isReportsView) => {
    const urlParams = new URLSearchParams(window.location.search);
    const _currentOrderByOption =
        urlParams.get(SEARCHER_TYPES.AI) === 'true' &&
        !isReportDetailsView &&
        !isReportsView &&
        !urlParams.get(SEARCHER_TYPES.OPTION)?.includes(SUB_STRING_CODE.VIDEOS)
            ? SEARCH_IMGS_ORDER_BY_OPTIONS.SIM
            : DEFAULT_ORDER_BY_OPTION;

    const currentSearcherObj = getCurrentSearcherObj();

    if (!currentSearcherObj) return _currentOrderByOption;

    return (
        currentSearcherObj[STORE_SEARCHER_FILTERS.ORDER_BY_VALUE] || _currentOrderByOption
    );
};

/**
 * Handle order by option selection.
 * @param {string} optionValue - The selected option value.
 * @param {Function} updateCurrentSearcherObj - Function to update searcher object.
 * @returns {void}
 */
export const handleOrderByOptionSelection = (optionValue, updateCurrentSearcherObj) => {
    const detailObj = {
        [SEARCH_IMG_QUERY_PARAMS.SORT_BY_AI]: optionValue,
        [SEARCH_IMG_QUERY_PARAMS.PAGE_AI]: 0,
        [SEARCH_IMG_QUERY_PARAMS.PAGE_REPORT_AI]: 0,
    };

    updateFiltersStateHistoryPage();
    updateFiltersStateHistoryPageReport();

    if (optionValue === SEARCH_IMGS_ORDER_BY_OPTIONS.SIM) {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set(SEARCHER_TYPES.AI, 'true');

        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.pushState({}, '', newUrl);

        document.dispatchEvent(new CustomEvent(UPDATE_AI_SEARCHER_EVENT));

        dispatchDetailIbj(detailObj);
    } else {
        dispatchDetailIbj(detailObj);
    }

    if (isReportDetailsView()) {
        document.dispatchEvent(
            new CustomEvent(UPDATE_VALUE_ORDER_BY_EVENT, {
                detail: {
                    value: optionValue,
                },
            }),
        );
    }

    updateCurrentSearcherObj();
};

/**
 * Handle filter synchronization event.
 * @param {string} typeFilterValue - The type filter value.
 * @param {string} currentOrderByOption - The current order by option.
 * @param {Function} updateCurrentSearcherObj - Function to update searcher object.
 * @returns {string} The updated order by option.
 */
export const handleFilterSync = (
    typeFilterValue,
    currentOrderByOption,
    updateCurrentSearcherObj,
) => {
    const urlParams = new URLSearchParams(window.location.search);
    let updatedOrderByOption = currentOrderByOption;

    if (!typeFilterValue.includes(SUB_STRING_CODE.CAT)) {
        if (isCreativeMode(urlParams) && urlParams.get(SEARCHER_TYPES.AI) === 'true') {
            updatedOrderByOption = SEARCH_IMGS_ORDER_BY_OPTIONS.SIM;
        }
        updateCurrentSearcherObj();
    }

    return updatedOrderByOption;
};

/**
 * Handle reset filters event.
 * @param {Function} updateCurrentSearcherObj - Function to update searcher object.
 * @returns {string} The reset order by option.
 */
export const handleResetFilters = (updateCurrentSearcherObj) => {
    const urlParams = new URLSearchParams(window.location.search);
    let resetOrderByOption;

    if (
        urlParams.has(SEARCHER_TYPES.IMG_UID) ||
        urlParams.has(SEARCHER_TYPES.IMAGE_ID) ||
        urlParams.get(SEARCHER_TYPES.AI) === 'true'
    ) {
        resetOrderByOption = SEARCH_IMGS_ORDER_BY_OPTIONS.SIM;
    } else {
        resetOrderByOption = DEFAULT_ORDER_BY_OPTION;
    }

    updateCurrentSearcherObj();
    return resetOrderByOption;
};

/**
 * Handle search image result event.
 * @param {Function} updateCurrentSearcherObj - Function to update searcher object.
 * @param {Function} handleTranslationHelper - Function to handle translation.
 * @returns {string} The updated order by option.
 */
export const handleSearchImageResult = (
    updateCurrentSearcherObj,
    handleTranslationHelper,
) => {
    const urlParams = new URLSearchParams(window.location.search);
    let updatedOrderByOption;

    if (urlParams.has(SEARCHER_TYPES.IMG_UID)) {
        updatedOrderByOption = SEARCH_IMGS_ORDER_BY_OPTIONS.SIM;
        updateCurrentSearcherObj();
    }

    handleTranslationHelper();
    return updatedOrderByOption;
};
